// @flow
import React from "react";
import GeoPointInput from "components/GeoPointInput";
import DropdownButton from "react-bootstrap/lib/DropdownButton";
import InputGroup from "react-bootstrap/lib/InputGroup";
import MenuItem from "react-bootstrap/lib/MenuItem";
import "font-awesome/css/font-awesome.css";
import FontAwesome from "react-fontawesome";
import styles from "./GeoRadiusInput.module.css";
import {
    ParseLocation,
    LocationToString,
    ParseRadius,
    RadiusToString,
} from "components/MapViewer/utils";
import SettingValue, { type SettingValueProps } from "containers/SettingValue";
import loc from "i18next";
import { type Location, type MapEditLocationResult } from "data/types";

type Props = {
    /** displayName of field */
    label: ?string,
    /** location:radius in string format */
    value: string,
    /** callback when user sets location
     * @param {string} locationWithRadius new location in string format
     */
    onChange: (locationWithRadius: string) => void,
    /** callback when user sets location by map
     * @param {string} locationWithRadius string format of location:radius
     */
    onManualLocation: (locationWithRadius: string) => void,
    /** persisted search radius */
    radius: SettingValueProps,
    /** placeholder text to display when @see {value} is empty */
    placeholder?: string,
};

type State = {
    location: ?Location,
    radius: number,
};

export class GeoRadiusInput extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = this._parseFromString(props.value, props.radius.value);
    }

    _parseFromString = (
        value: string,
        radiusIn: ?number
    ): { location: string, radius: number } => {
        const locRadius = ParseRadius(value);
        return {
            location: locRadius.location,
            radius: locRadius.radius || radiusIn || 500,
        };
    };

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        /* istanbul ignore else */
        if (this.props.value !== nextProps.value) {
            this.setState(
                this._parseFromString(nextProps.value, nextProps.radius.value)
            );
        }
    }

    _displayUnit = (meter: number) =>
        meter < 1000 ? `${meter}m` : `${(meter / 1000).toFixed(0)} km`;

    _fireChangedValue = () =>
        this.props.onChange(
            RadiusToString(this.state.location, this.state.radius)
        );

    _onChange = (location: string) =>
        this.setState(
            { location: ParseLocation(location) },
            this._fireChangedValue
        );

    _onRadiusChange = (radius: number) =>
        this.setState({ radius }, () => {
            if (this.state.location != null) this._fireChangedValue();
            this.props.radius.setValue(radius);
        });

    _onManualLocation = (editLocationResult: MapEditLocationResult) =>
        this.setState(
            {
                location: editLocationResult.location,
                radius: editLocationResult.radius,
            },
            () =>
                this.props.onManualLocation(
                    RadiusToString(
                        editLocationResult.location,
                        editLocationResult.radius
                    )
                )
        );

    render = () => (
        <GeoPointInput
            {...this.props}
            value={LocationToString(this.state.location)}
            radius={this.state.radius}
            showIcon={false}
            onChange={this._onChange}
            onManualLocation={this._onManualLocation}
            placeholder={this.props.placeholder}
        >
            <DropdownButton
                id="geo-search-radius"
                className={styles.dropdownButton}
                pullRight
                componentClass={InputGroup.Button}
                title={
                    <>
                        <span
                            className={styles.radius}
                            title={loc.t("common:location.setRadius")}
                        >
                            {this._displayUnit(this.state.radius)}
                        </span>
                        <FontAwesome name="street-view" fixedWidth />
                    </>
                }
            >
                {window.CONFIG.general.geo.radius.searchRadius.map((r) => (
                    <MenuItem
                        key={`geosr-${r}`}
                        className={styles.unit}
                        active={r === this.state.radius}
                        onSelect={
                            /* istanbul ignore next */ () =>
                                this._onRadiusChange(r)
                        }
                    >
                        {this._displayUnit(r)}
                    </MenuItem>
                ))}
            </DropdownButton>
        </GeoPointInput>
    );
}
export default SettingValue({ name: "geoSearchRadius", namespace: "radius" })(
    GeoRadiusInput
);
